"use strict";
// Copyright (c) 2023-2025, Brandon Lehmann <brandonlehmann@gmail.com>
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeInit = exports.toURLSearchParams = exports.HTTP_METHOD_BODY_REMOVE = exports.HTTP_METHODS = void 0;
const cross_fetch_1 = require("cross-fetch");
const buffer_1 = require("buffer");
/** @ignore */
exports.HTTP_METHODS = [
    'GET',
    'HEAD',
    'POST',
    'PUT',
    'DELETE',
    'CONNECT',
    'OPTIONS',
    'TRACE',
    'PATCH',
    'PROPFIND',
    'REPORT',
    'PROPPATCH',
    'MKCOL',
    'MKCALENDAR',
    'COPY',
    'MOVE',
    'LOCK',
    'UNLOCK',
    'ACL',
    'SEARCH',
    'QUERY',
    'BIND',
    'UNBIND',
    'REBIND'
];
/** @ignore */
exports.HTTP_METHOD_BODY_REMOVE = [
    'GET',
    'HEAD',
    'OPTIONS',
    'TRACE',
    'CONNECT'
];
/**
 * Converts the given object into a URLSearchParms object
 *
 * Note: only the top-level structure is handled
 * @param obj
 */
const toURLSearchParams = (obj) => {
    const params = new URLSearchParams();
    for (const key of Object.keys(obj)) {
        if (typeof obj[key] === 'undefined') {
            params.set(key, '');
        }
        else if (typeof obj[key] === 'object') {
            params.set(key, JSON.stringify(obj[key]));
        }
        else {
            params.set(key, obj[key].toString());
        }
    }
    return params;
};
exports.toURLSearchParams = toURLSearchParams;
/**
 * Shared init normalization helper
 * @param init
 */
const normalizeInit = (init = {}) => {
    init.method ??= 'GET';
    init.method = init.method.toUpperCase();
    init.rejectUnauthorized ??= true;
    init.headers ??= new cross_fetch_1.Headers(); // default to empty headers
    if (!exports.HTTP_METHODS.includes(init.method)) {
        throw new Error(`Invalid method: ${init.method}`);
    }
    // if the headers are NOT an instance of Headers, then we need to convert them
    if (!(init.headers instanceof cross_fetch_1.Headers)) {
        const headers = new cross_fetch_1.Headers();
        if (Array.isArray(init.headers)) {
            for (const [header, value] of init.headers) {
                headers.set(header, value);
            }
        }
        else if (typeof init.headers === 'object') {
            for (const header of Object.keys(init.headers)) {
                headers.set(header, init.headers[header]);
            }
        }
        else {
            throw new Error('headers must be an array, object, or instance of Headers');
        }
        init.headers = headers;
    }
    if (init.username && init.password) {
        const auth = buffer_1.Buffer.from(`${init.username}:${init.password}`).toString('base64');
        init.headers.set('authorization', `Basic ${auth}`);
    }
    else if (init.bearer) {
        init.headers.set('authorization', `Bearer ${init.bearer}`);
    }
    else if (init.jwt) {
        init.headers.set('authorization', `Bearer ${init.jwt}`);
    }
    if (init.formData) {
        init.headers.set('content-type', 'application/x-www-form-urlencoded');
        if (!(init.formData instanceof URLSearchParams)) {
            init.formData = (0, exports.toURLSearchParams)(init.formData);
        }
        init.body = init.formData;
    }
    else if (init.json) {
        init.headers.set('content-type', 'application/json');
        if (typeof init.json !== 'string') {
            init.json = JSON.stringify(init.json);
        }
        init.body = init.json;
    }
    if (exports.HTTP_METHOD_BODY_REMOVE.includes(init.method)) {
        delete init.body;
    }
    return init;
};
exports.normalizeInit = normalizeInit;
